/**
 * useAsset Hook
 *
 * Provides cache-busted URLs for public folder assets.
 * Uses the public-assets-manifest.json generated at build time.
 *
 * Usage:
 *   const logoSrc = useAsset('/tailtations-white.svg')
 *   <img src={logoSrc} alt="Logo" />
 */

import { useMemo } from 'react'
// @ts-ignore - manifest is generated at build time
import manifestData from '../public-assets-manifest.json'

// Import manifest - will be generated at build time
const manifest: Record<string, { versionedPath: string }> = manifestData || {}

/**
 * Get versioned URL for a public asset
 * @param assetPath - Path to asset relative to public folder (e.g., '/logo.png')
 * @returns Versioned asset URL with cache-busting query parameter
 */
export function useAsset(assetPath: string): string {
  return useMemo(() => {
    // Ensure path starts with /
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`

    // Return versioned path if available
    if (manifest[normalizedPath]) {
      return manifest[normalizedPath].versionedPath
    }

    // Fallback: return original path (dev mode or missing manifest)
    if (import.meta.env.DEV) {
      return normalizedPath
    }

    // Production warning
    console.warn(`Asset not found in manifest: ${normalizedPath}`)
    return normalizedPath
  }, [assetPath])
}

/**
 * Get versioned URL for a public asset (non-hook version)
 * Use this in non-React contexts or when hooks aren't available
 */
export function getAssetUrl(assetPath: string): string {
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`

  if (manifest[normalizedPath]) {
    return manifest[normalizedPath].versionedPath
  }

  return normalizedPath
}

/**
 * Preload an asset to improve performance
 */
export function preloadAsset(assetPath: string, type: 'image' | 'video' | 'font' = 'image') {
  const url = getAssetUrl(assetPath)
  const link = document.createElement('link')
  link.rel = 'preload'
  link.href = url

  switch (type) {
    case 'image':
      link.as = 'image'
      break
    case 'video':
      link.as = 'video'
      break
    case 'font':
      link.as = 'font'
      link.crossOrigin = 'anonymous'
      break
  }

  document.head.appendChild(link)
}

export default useAsset
